import { flow } from '@1x2network/flow'
import { BasicLayout, OrientationMap, Orientor, scaleToLongest } from '@1x2network/layout'
import { Component, ContainerSpecification, createSpine, Spine, SpineSpecification } from '@1x2network/pixi_assets'
import { Container, ISize } from 'pixi.js'

/**
 * Animation to hide transition between base and bonus game
 * @author Jordan Harrison - <jordan.harrison@1x2network.com>
 * @version 1.0.0
 */
export class Transition extends Component<TransitionSpecification> {
    private spines: OrientationMap<Spine>
    private orientor: Orientor<Spine>
    private layout: BasicLayout

    constructor(parent: Container, layout: BasicLayout) {
        super({ spec: 'transition', parent })
        this.layout = layout
        this.visible = true
        this.spines = {
            landscape: createSpine({ spec: this.specification.spine, parent: this }),
            portrait: createSpine({ spec: this.specification.spine, parent: this }),
        }

        // Update renderable state of spines based on orientation (Don't change visible as we want the animations to play)
        this.orientor = new Orientor({
            layout,
            property: this.spines,
            onOrient: (active, inactive) => {
                active.renderable = true
                inactive.renderable = false
            },
        })

        // Position and scale the spine based on the layout size
        this.layout.onResize.connect(() => {
            const { size, left, right, top, bottom, orientation } = this.layout
            const centre = { x: (left + right) / 2, y: (top + bottom) / 2 }

            const spine = this.spines[orientation]
            const spineSize = this.specification.size[orientation]
            spine.scale.set(scaleToLongest(spineSize, size))
            spine.position.copyFrom(centre)
        })

        this.visible = false
    }

    /**
     * Play an animation on both spines
     * @param animation Animation key to play, either 'in', 'loop', or 'out'
     * @param loop Whether the animation should loop
     * @returns Promise that resolves when both animations have completed
     */
    private playSpineAnimation(animation: 'in' | 'out', loop: boolean) {
        return Promise.all([
            flow.wait(this.spines.landscape.play(this.specification.animations.landscape[animation], 0, loop)),
            flow.wait(this.spines.portrait.play(this.specification.animations.portrait[animation], 0, loop)),
        ])
    }

    transitionIn() {
        this.visible = true
        return this.playSpineAnimation('in', false)
    }

    transitionOut() {
        return this.playSpineAnimation('out', false)
    }

    async animate() {
        // Reset lastTime on both spines since visible is set to false
        this.orientor.call((spine) => spine.reset())

        await this.transitionIn()
        await flow.wait(1000)
        await this.transitionOut()
        this.visible = false
    }
}

export interface TransitionSpecification extends ContainerSpecification {
    spine: SpineSpecification
    animations: OrientationMap<{
        in: string
        out: string
    }>
    size: OrientationMap<ISize>
}
